import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import styles from './OverlayScrollbar.module.scss'

/** Which way the content this bar drives runs. */
type Axis = 'y' | 'x'

interface OverlayScrollbarProps {
  /** The scroll container this bar drives */
  targetRef: RefObject<HTMLElement | null>
  /**
   * Vertical unless said otherwise, which is every bar in the app but the
   * website builder's row of photos.
   */
  axis?: Axis
  /**
   * Told whenever the content starts or stops overflowing, for a container
   * that keeps room for the bar only while there is one to show.
   */
  onScrollableChange?: (scrollable: boolean) => void
}

interface Metrics {
  /** Thumb offset and length as a share of the track, 0-100 */
  offset: number
  length: number
  scrollable: boolean
}

const MIN_THUMB_PERCENT = 8

/**
 * A scrollbar drawn ON TOP of the content instead of beside it.
 *
 * A native `::-webkit-scrollbar` occupies layout space, so widening it on hover
 * reflows everything it sits next to, in a week calendar that shifts all seven
 * day columns under the pointer. This one is an absolutely positioned thumb, so
 * the content keeps the full width and the bar can grow over it.
 *
 * It is also the only bar this app can count on being SEEN: macOS hides a native
 * overlay bar until something scrolls, which leaves a row that runs off its right
 * edge looking like it simply ends there.
 */
function OverlayScrollbar(props: OverlayScrollbarProps) {
  const { targetRef, axis = 'y', onScrollableChange } = props
  const vertical = axis === 'y'

  const [metrics, setMetrics] = useState<Metrics>({
    offset: 0,
    length: 0,
    scrollable: false
  })
  const [isDragging, setIsDragging] = useState(false)

  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    pointerId: number
    origin: number
    originScroll: number
  } | null>(null)

  const measure = useCallback(() => {
    const element = targetRef.current
    if (!element) return

    const total = vertical ? element.scrollHeight : element.scrollWidth
    const visible = vertical ? element.clientHeight : element.clientWidth
    const position = vertical ? element.scrollTop : element.scrollLeft
    const scrollable = total > visible + 1

    setMetrics({
      scrollable,
      length: scrollable
        ? Math.max((visible / total) * 100, MIN_THUMB_PERCENT)
        : 0,
      offset: scrollable ? (position / total) * 100 : 0
    })
  }, [targetRef, vertical])

  useEffect(() => {
    const element = targetRef.current
    if (!element) return undefined

    measure()

    element.addEventListener('scroll', measure, { passive: true })

    // Content grows and shrinks with the window and with the shifts rendered
    // into it, so the thumb is re-measured from both.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    Array.from(element.children).forEach(child => observer.observe(child))

    return () => {
      element.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [targetRef, measure])

  useEffect(() => {
    onScrollableChange?.(metrics.scrollable)
  }, [metrics.scrollable, onScrollableChange])

  useEffect(() => {
    if (!isDragging) return undefined

    const handleMove = (event: PointerEvent) => {
      const drag = dragRef.current
      const element = targetRef.current
      const track = trackRef.current
      if (!drag || !element || !track || drag.pointerId !== event.pointerId) {
        return
      }

      const bounds = track.getBoundingClientRect()
      const trackLength = vertical ? bounds.height : bounds.width
      if (!trackLength) return

      // A pixel of track is worth scrollLength/trackLength pixels of content
      const total = vertical ? element.scrollHeight : element.scrollWidth
      const travelled =
        (vertical ? event.clientY : event.clientX) - drag.origin
      const next = drag.originScroll + travelled * (total / trackLength)

      if (vertical) element.scrollTop = next
      else element.scrollLeft = next
    }

    const handleUp = (event: PointerEvent) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      dragRef.current = null
      setIsDragging(false)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)

    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
    }
  }, [isDragging, targetRef, vertical])

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = targetRef.current
    if (event.button !== 0 || !element) return

    event.preventDefault()
    dragRef.current = {
      pointerId: event.pointerId,
      origin: vertical ? event.clientY : event.clientX,
      originScroll: vertical ? element.scrollTop : element.scrollLeft
    }
    setIsDragging(true)
  }

  if (!metrics.scrollable) return null

  return (
    <div
      ref={trackRef}
      className={`${styles.track} ${vertical ? styles.trackY : styles.trackX} ${
        isDragging ? styles.trackActive : ''
      }`}
      aria-hidden="true"
    >
      <div
        className={`${styles.thumb} ${vertical ? styles.thumbY : styles.thumbX} ${
          isDragging ? styles.thumbActive : ''
        }`}
        // Computed geometry, so it cannot be a class.
        style={
          vertical
            ? { top: `${metrics.offset}%`, height: `${metrics.length}%` }
            : { left: `${metrics.offset}%`, width: `${metrics.length}%` }
        }
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  )
}

export default OverlayScrollbar
